import { useState } from "react";
import { useGetDealTrackerDataQuery } from "../../store/apiSlices/dealTracker";
import { DealTarckerTables } from "../../types/DealTracker";

function calculateTotals(tables?: DealTarckerTables) {
  // Default totals
  let pendingReceivables = 0;
  let paidReceivables = 0;
  let pendingPayables = 0;
  let paidPayables = 0;

  if (!tables?.data || tables.data.length === 0) {
    return {
      pendingReceivables,
      paidReceivables,
      pendingPayables,
      paidPayables,
    };
  }

  tables.data.forEach((table) => {
    if (!table.complete_json) return;

    table.complete_json.forEach((row) => {
      const receivable = parseFloat(row?.receivables?.column_value || "0") || 0;
      const receivableStatus = row?.receivables_status?.column_value || "";

      if (receivable > 0) {
        if (receivableStatus === "Pending") pendingReceivables += receivable;
        if (receivableStatus === "Paid") paidReceivables += receivable;
      }

      const payable = parseFloat(row?.payables?.column_value || "0") || 0;
      const payableStatus = row?.payables_status?.column_value || "";

      if (payable > 0) {
        if (payableStatus === "Pending") pendingPayables += payable;
        if (payableStatus === "Paid") paidPayables += payable;
      }
    });
  });

  return { pendingReceivables, paidReceivables, pendingPayables, paidPayables };
}

export default function ReceivablePieChart() {
  const { data: tables, isLoading } = useGetDealTrackerDataQuery();

  const { pendingReceivables, paidReceivables, paidPayables, pendingPayables } =
    calculateTotals(tables);

  const [view, setView] = useState<"receivables" | "payables">("receivables");

  const pending = view === "receivables" ? pendingReceivables : pendingPayables;
  const paid = view === "receivables" ? paidReceivables : paidPayables;
  const total = pending + paid;

  return (
    <section id="receivable-chart" className="card h-[269px] w-full">
      {isLoading && <ChartSkeleton />}

      {tables && (
        <>
          {/* Header with toggle */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-medium text-gray-900">
              {view === "receivables" ? "Receivables" : "Payables"}
            </h2>
            <button
              onClick={() =>
                setView(view === "receivables" ? "payables" : "receivables")
              }
              className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100"
            >
              Switch to {view === "receivables" ? "Payables" : "Receivables"}
            </button>
          </div>

          {total > 0 ? (
            <svg viewBox="0 0 40 40" className="w-40 h-40 mx-auto">
              {/* Pending */}
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="transparent"
                stroke="currentColor"
                strokeWidth="6"
                strokeDasharray={`${(pending / total) * 100} ${
                  100 - (pending / total) * 100
                }`}
                strokeDashoffset="25"
                className="text-blue-400 transition-all duration-700 ease-in-out"
              />
              {/* Paid */}
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="transparent"
                stroke="currentColor"
                strokeWidth="6"
                strokeDasharray={`${(paid / total) * 100} ${
                  100 - (paid / total) * 100
                }`}
                strokeDashoffset={25 - (pending / total) * 100}
                className="text-blue-900 transition-all duration-700 ease-in-out"
              />
              {/* Center Text */}
              <text
                x="20"
                y="20"
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-gray-700 transition-all duration-500"
                fontSize="4"
                fontWeight="600"
              >
                ${total}
              </text>
            </svg>
          ) : (
            <p className="text-center text-gray-500">
              No {view === "receivables" ? "receivables" : "payables"} data
            </p>
          )}

          {/* Legend */}
          <div className="mt-4 flex space-x-4 items-center justify-center">
            <div className="flex items-center">
              <span className="inline-block w-3 h-3 rounded-full mr-2 bg-blue-400" />
              <span className="text-xs text-gray-700">Pending: ${pending}</span>
            </div>
            <div className="flex items-center">
              <span className="inline-block w-3 h-3 rounded-full mr-2 bg-blue-900" />
              <span className="text-xs text-gray-700">Paid: ${paid}</span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ChartSkeleton() {
  return (
    <div className="animate-pulse bg-slate-300 rounded w-full h-[235px]"></div>
  );
}
